<template>
  <div class="app-blank-create__root" @click="onAppBlankClick">
    <div class="content">
      <IconMyIconBlankAppAdd></IconMyIconBlankAppAdd>
      <div class="label">从空白创建</div>
    </div>
  </div>

  <el-dialog
    v-if="!dialogDestroy"
    v-model="dialogVisible"
    customClass="dialog-common workspace-blank-app-create"
    appendToBody
    :showClose="false"
    :closeOnClickModal="false"
    @closed="handleClosed"
  >
    <workspace-app-creating
      :platformId="platformId"
      :workspaceId="workspaceId"
      @on-workspace-app-created="onWorkspaceAppCreated"
    ></workspace-app-creating>
  </el-dialog>
</template>

<script lang="ts" setup>
import { ReqMiddlePageAppPreCheck } from '@vas2t-lowcode/api';
import { computed, nextTick, ref } from 'vue';
import { getWorkbenchEmitter } from '../workbench-info/workbench-info.tools';
import { TMiddlePageOpenAppQuery } from '../middle-page-open-app/middle-page-open-app';
import { getMiddlePageAppUrl } from '../middle-page-open-app/middle-page-open-app.router';
import { openInNewTab } from '@/utils/open-in-new-tab';
import { useRouter } from 'vue-router';
import { usePTeamStore, usePUserStore } from '@/store';
import { showLoginHandler } from '@/utils/login-by-env';
import { showMessage } from '@vas2t-lowcode/utils';
import WorkspaceAppCreating from '@/features/workspace-app/workspace-app-creating.vue';
import { getSelectedTemplate } from './app-list.tool';
import { dwOnCreateBlankApp, dwOnCreateBlankAppSuccess } from './app-list.dw';

const teamStore = usePTeamStore();
/** 工作区id */
const workspaceId = computed(() => {
  return teamStore.workbenchId || 0;
});
/** 低代码平台ID-暂时写死 */
const platformId = import.meta.env.VITE_APP_PLATFORM_ID_QL;

/** 弹窗组件销毁 */
const dialogDestroy = ref(true);

const dialogVisible = ref(false);
/** 创建应用状态loading */
const isCreatingApp = ref(false);
/** 创建应用成功 */
const isCreatedAppSuccess = ref(false);
/** 应用数据-创建应用成功 */
const appData = ref<ReqMiddlePageAppPreCheck>();
/** 刷新工作区emitter */
const workbenchInfoEmitter = getWorkbenchEmitter();

/** 工作区创建成功回调事件 */
const onWorkspaceAppCreated = (data?: ReqMiddlePageAppPreCheck) => {
  // 打开中间页
  if (!data) {
    // 应用创建失败，关闭当前弹窗
    isCreatingApp.value = false;
    // 标记当前创建应用是否成功的状态
    isCreatedAppSuccess.value = false;
    // 关闭弹窗
    dialogVisible.value = false;
    return;
  }

  appData.value = data;
  // 关闭创建中状态
  isCreatingApp.value = false;
  // 显示创建应用成功内容
  isCreatedAppSuccess.value = true;
  // 关闭弹窗
  dialogVisible.value = false;

  // 埋点
  const selectedTemplateType = getSelectedTemplate();
  dwOnCreateBlankAppSuccess(selectedTemplateType?.name || '');

  nextTick(() => {
    // 通知刷新工作区信息
    workbenchInfoEmitter.emit('get-team-config', true);
    // 打开中间页
    openBlankAppInTab();
  });
};

const router = useRouter();
/** 打开空白应用在新标签页 */
const openBlankAppInTab = () => {
  if (!appData.value?.third_app_id) {
    return;
  }

  const { platform_id, third_app_id, third_ws_id, team_id } = appData.value;
  const queryData = {
    page_type: 'open',
    platform_id,
    team_id,
    third_app_id,
    third_ws_id: encodeURIComponent(third_ws_id || ''),
  } as TMiddlePageOpenAppQuery;
  const url = getMiddlePageAppUrl(router, queryData) || '';
  const params = { url, appid: platform_id || '', src: '', extraData: queryData };
  // 新tab页打开
  openInNewTab(params);
};

/** 是否为工作区创建者 */
const hasCreateBlankAppPermission = computed(() => {
  return teamStore.isWorkbenchOwner || teamStore.isWorkbenchManager;
});

const userStore = usePUserStore();

const onAppBlankClick = () => {
  // #region 埋点
  const selectedTemplateType = getSelectedTemplate();
  dwOnCreateBlankApp(selectedTemplateType?.name || '');
  // #endregion

  // #region 创建前检查

  // 登录检查
  if (!userStore.isLogin) {
    showLoginHandler();
    return;
  }
  // 根据当前工作区角色判断创建权限
  if (!hasCreateBlankAppPermission.value && !!workspaceId.value) {
    showMessage({ msgContent: '暂无权限，请联系工作区拥有者或切换至自己的工作区', msgType: 'error' });
    return;
  }
  // #endregion

  // 以上条件符合，则创建应用
  isCreatingApp.value = true;
  nextTick(() => {
    // 显示loading弹窗组件
    dialogVisible.value = true;
    dialogDestroy.value = false;
  });
};

const emit = defineEmits<{
  /** 通知父组件关闭弹窗 */
  (e: 'on-workspace-blank-app-create-dialog-closed'): void;
}>();
const handleClosed = () => {
  // 销毁弹窗组件
  dialogDestroy.value = true;

  emit('on-workspace-blank-app-create-dialog-closed');
};
</script>

<style lang="scss" scoped>
.app-blank-create {
  &__root {
    cursor: pointer;
    // height: 137px;
    // width: 200px;
    cursor: pointer;
    box-sizing: border-box;
    width: 100%;
    background: var(--lowcode-background-01);
    border-radius: 8px;
    border: 1px solid var(--lowcode-ctl-border-04);
    position: relative;
    padding: 20px 20px 13px 20px;
    transition: box-shadow ease-in-out 0.25s;
    display: flex;
    align-items: center;
    justify-content: center;

    .content {
      text-align: center;
    }

    .label {
      margin-top: 8px;
      height: 22px;
      font-size: 12px;
      color: var(--lowcode-text-02);
      line-height: 22px;
    }

    &:hover {
      box-shadow: 0px 4px 12px 0px var(--lowcode-custom-border-11);
    }
  }
}
</style>

<style lang="scss">
.workspace-app-create.dialog-common {
  .el-dialog {
    &__header {
      display: none;
    }
    &__body {
      padding: 32px 0;
    }
  }
}
</style>
